/* eslint-disable react/display-name */
import TopNav from '@components/Nav/TopNav';
import TipDirectLeftMenu from '@components/Nav/TopNav/TipDirectLeftMenu';
import TextIconInformationBox from '@components/Onboarding/TextIconInformationBox';
import { TextIconBoxType } from '@components/Onboarding/constants';
import localizationMessages from '@components/Resources/ResourcesTable/messages';
import AccessControlledButton from '@components/TapToTip/AccessControlledButton';
import PaymentLinkQRGeneratorModal from '@components/TapToTip/PaymentLinksQRGeneratorModal';
import { Paper, useMediaQuery } from '@material-ui/core';
import GridOnIcon from '@material-ui/icons/GridOn';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { makeStyles } from '@material-ui/styles';
import AccountPaymentsUI from './StripeConnectPortal/Payments';
import { StyleBreakpoints, UserRoles } from '@utils/constants';
import {
  PaymentsView,
  canSwitchPaymentsView,
  formatMoney,
  formatMoneyAndCurrencySymbol,
  hasOwnStripePaymentsView,
  isPaymentsCompany
} from '@utils/helpers';
import { useIsFeatureForCompanyEnabled } from '@utils/hooks';
import { useInjectReducer } from '@utils/injectReducer';
import { useInjectSaga } from '@utils/injectSaga';
import { format } from 'date-fns';
import { CsvBuilder } from 'filefy';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import MaterialTable from 'material-table';
import { default as React, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import * as actions from './actions';
import { TransactionStatusType } from './constants';
import messages from './messages';
import reducer from './reducer';
import saga from './saga';

const useStyles = makeStyles(theme => ({
  // Blends the material-table into the surrounding slate-50 card: transparent
  // toolbar, a single header divider, no per-row / bottom borders, and a subtle
  // blue-grey zebra stripe (the old #FAF7FA read as a warning/error tint).
  tableSurface: {
    background: 'transparent',
    boxShadow: 'none',
    '& .MuiToolbar-root': {
      backgroundColor: 'transparent'
    },
    '& .MuiTableCell-root': {
      borderBottom: 'none'
    },
    '& .MuiTableCell-head': {
      backgroundColor: '#f8fafc',
      borderBottom: '1px solid #e2e8f0',
      color: '#475569'
    },
    '& .MuiTableBody-root .MuiTableRow-root:nth-child(even)': {
      backgroundColor: '#f1f5f9'
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      backgroundColor: '#eef2f7'
    },
    '& .MuiTablePagination-root': {
      backgroundColor: 'transparent'
    },
    '& .MuiTableFooter-root .MuiTableCell-root': {
      borderTop: 'none',
      borderBottom: 'none'
    }
  },
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      flexDirection: 'row'
    }
  }
}));

const transactionStatusBadgeClasses = {
  pending: 'bg-slate-100 text-slate-600 border-slate-200/55',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200/55',
  failed: 'bg-red-50 text-red-700 border-red-200/55'
};

// eslint-disable-next-line no-unused-vars
const TransactionStatus = ({ status, classes }) => {
  return (
    <div
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold border border-solid ${transactionStatusBadgeClasses[
        status
      ] || transactionStatusBadgeClasses.pending}`}>
      {TransactionStatusType[status]}
    </div>
  );
};

const TippingPayments = ({ transactions, fetchTransactions, user, isLoading }) => {
  const intl = useIntl();
  const classes = useStyles();
  const isMobile = useMediaQuery(`(max-width:${StyleBreakpoints.sm}px)`);
  const [isPaymentLnkModalOpen, setIsPaymentLinkModalOpen] = useState(false);
  const isFeatureForCompanyEnabled = useIsFeatureForCompanyEnabled();

  const userIsPaymentsCompany = isPaymentsCompany(user.companyId, user)

  // Defaults to the admin's own payments so the page opens where it does today;
  // the switcher is the way back to the company-wide list.
  const canSwitchView = canSwitchPaymentsView(user);
  const [paymentsView, setPaymentsView] = useState(PaymentsView.OWN_STRIPE);

  useInjectReducer({ key: 'tipPayments', reducer });
  useInjectSaga({ key: 'tipPayments', saga });

  useEffect(() => {
    if (!transactions?.length) {
      fetchTransactions(null);
    }
  }, []);

  const paymentCompanyColumns = [
    {
      field: 'status',
      title: intl.formatMessage(messages.status),
      sorting: false,
      render: rowData => <TransactionStatus status={rowData.status} classes={classes} />
    },
    {
      field: 'type',
      title: intl.formatMessage(messages.type),
      sorting: true,
      render: rowData => intl.formatMessage(messages[`${rowData.type || 'tip'}Type`]),
      hidden: !isFeatureForCompanyEnabled('PaymentLinks')
    },
    {
      field: 'currency',
      title: 'Currency',
      render: rowData => (rowData.paymentDetails?.tripadmitValues?.currencyGuideAskedFor || rowData.paymentCurrency || rowData.convertedTip?.currency || '').toUpperCase(),
      hidden: !isFeatureForCompanyEnabled('PaymentLinks')
    },
    {
      field: 'amount',
      title: 'Amount',
      sorting: true,
      render: rowData => {
        const amount = rowData.paymentDetails?.tripadmitValues?.amountOfTransactionGuideAskedFor || rowData.paymentValue || rowData.amount;
        const currency = rowData.paymentDetails?.tripadmitValues?.currencyGuideAskedFor || rowData.paymentCurrency || rowData.convertedTip?.currency;
        return formatMoney({ currency: currency?.toUpperCase(), amount });
      },
      hidden: !isFeatureForCompanyEnabled('PaymentLinks')
    },
    {
      field: 'date',
      title: isMobile ? 'Date' : intl.formatMessage(messages.activityDate),
      cellStyle: {
        fontWeight: 'bold',
        fontSize: isMobile ? '12px' : 'inherit',
        color: '#b0b0b0'
      },
      render: rowData => format(new Date(rowData.date), 'dd-MM-yyyy')
    },
    {
      field: 'time',
      title: intl.formatMessage(messages.time),
      sorting: false,
      render: rowData =>
        new Date(rowData.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },/*
    {
      field: 'customerName',
      title: intl.formatMessage(messages.customerName),
      render: rowData => `${rowData.payerDetails?.name || ''}`,
      hidden:
        !user.companyDetails?.features?.tipPageSettings?.tip?.name &&
        !user.companyDetails?.features?.tipPageSettings?.payment?.name
    },*/
    {
      field: 'transactionId',
      title: intl.formatMessage(messages.reference),
      hidden: false
    },
    {
      field: 'name',
      title: intl.formatMessage(messages.name),
      render: rowData => `${rowData.firstName} ${rowData.lastName}`,
      customFilterAndSearch: (term, rowData) => {
        return (
          `${rowData.firstName.toLowerCase()} ${rowData.lastName.toLowerCase()}`.indexOf(
            term.toLowerCase()
          ) != -1
        );
      },
      hidden:
        user.role !== UserRoles.admin ||
        user.companyDetails?.features?.disableCompanyTransactionReporting
    },
    {
      field: 'tag',
      title: 'Tag',
      hidden: false
    },
  ]

  const columns = [
    {
      field: 'status',
      title: intl.formatMessage(messages.status),
      sorting: false,
      render: rowData => <TransactionStatus status={rowData.status} classes={classes} />
    },
    {
      field: 'type',
      title: intl.formatMessage(messages.type),
      sorting: true,
      render: rowData => intl.formatMessage(messages[`${rowData.type || 'tip'}Type`]),
      hidden: !isFeatureForCompanyEnabled('PaymentLinks')
    },
    {
      field: 'payoutCurrency',
      title: 'Payout Currency',
      render: rowData => rowData.convertedTip?.currency || rowData.currency,
      hidden: true
    },
    {
      field: 'convertedTip',
      title: intl.formatMessage(messages.convertedTip),
      cellStyle: {
        fontWeight: 'bold',
        fontSize: isMobile ? '12px' : 'inherit'
      },
      render: rowData =>
        // convertedTip is optional on transactions (backend falls back to the raw
        // amount/currency the same way in its metrics aggregation)
        formatMoneyAndCurrencySymbol({
          amount: rowData.convertedTip?.amount ?? rowData.amount,
          currency: rowData.convertedTip?.currency || rowData.currency
        })
    },
    {
      field: 'date',
      title: isMobile ? 'Date' : intl.formatMessage(messages.activityDate),
      cellStyle: {
        fontWeight: 'bold',
        fontSize: isMobile ? '12px' : 'inherit',
        color: '#b0b0b0'
      },
      render: rowData => format(new Date(rowData.date), 'dd-MM-yyyy')
    },
    {
      field: 'time',
      title: intl.formatMessage(messages.time),
      sorting: false,
      render: rowData =>
        new Date(rowData.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    {
      field: 'customerName',
      title: intl.formatMessage(messages.customerName),
      render: rowData => `${rowData.payerDetails?.name || ''}`,
      hidden:
        !user.companyDetails?.features?.tipPageSettings?.tip?.name &&
        !user.companyDetails?.features?.tipPageSettings?.payment?.name
    },
    {
      field: 'name',
      title: intl.formatMessage(messages.name),
      render: rowData => `${rowData.firstName} ${rowData.lastName}`,
      customFilterAndSearch: (term, rowData) => {
        return (
          `${rowData.firstName.toLowerCase()} ${rowData.lastName.toLowerCase()}`.indexOf(
            term.toLowerCase()
          ) != -1
        );
      },
      hidden:
        user.role !== UserRoles.admin ||
        user.companyDetails?.features?.disableCompanyTransactionReporting
    },
    {
      field: 'userId',
      title: intl.formatMessage(messages.userId),
      hidden:
        user.role !== UserRoles.admin ||
        user.companyDetails?.features?.disableCompanyTransactionReporting
    },
    {
      field: 'transactionId',
      title: intl.formatMessage(messages.reference),
      hidden: true
    },
    {
      field: 'currency',
      title: 'Tip Currency',
      hidden: true
    },
    {
      field: 'amountAfterProcessingFees',
      title: intl.formatMessage(messages.revenue),
      cellStyle: {
        fontSize: isMobile ? '12px' : 'inherit'
      },
      render: rowData =>
        formatMoneyAndCurrencySymbol({
          amount:
            rowData.type === 'payment'
              ? rowData.paymentValue || rowData.amount
              : rowData.amountAfterProcessingFees,
          currency:
            rowData.type === 'payment' && rowData.paymentCurrency
              ? rowData.paymentCurrency
              : rowData.currency
        })
    },
    {
      field: 'amount',
      title: 'Gross Amount',
      hidden: true
    }
  ];

  const getColumnValueForExport = (rowData, columnDef) => {
    if (columnDef.hidden) return; // Skip hidden columns
    switch (columnDef.field) {
      case 'status':
        return intl.formatMessage(messages[rowData.status || 'success']);
      case 'type':
        return rowData.type;
      case 'payoutCurrency':
        return rowData.convertedTip?.currency?.toUpperCase() || '';
      case 'currency':
        return (rowData.paymentDetails?.tripadmitValues?.currencyGuideAskedFor || rowData.paymentCurrency || rowData.convertedTip?.currency || rowData.currency || '').toUpperCase();
      case 'amountAfterProcessingFees':
        return formatMoneyAndCurrencySymbol({
          amount:
            rowData.type === 'payment'
              ? rowData.paymentDetails?.tripadmitValues?.amountOfTransactionGuideAskedFor || rowData.paymentValue || rowData.amount
              : rowData.amountAfterProcessingFees,
          currency:
            rowData.type === 'payment' && (rowData.paymentDetails?.tripadmitValues?.currencyGuideAskedFor || rowData.paymentCurrency)
              ? rowData.paymentDetails?.tripadmitValues?.currencyGuideAskedFor || rowData.paymentCurrency
              : rowData.currency
        });
      case 'convertedTip':
        return formatMoneyAndCurrencySymbol({
          amount: rowData.convertedTip?.amount,
          currency: rowData.convertedTip?.currency
        });
      case 'amount':
        return formatMoney({
          amount: rowData.paymentDetails?.tripadmitValues?.amountOfTransactionGuideAskedFor || rowData.paymentValue || rowData.amount,
          currency: (rowData.paymentDetails?.tripadmitValues?.currencyGuideAskedFor || rowData.paymentCurrency || rowData.convertedTip?.currency || '').toUpperCase()
        });
      case 'date':
        return format(new Date(rowData.date), 'dd-MM-yyyy');
      case 'time':
        return new Date(rowData.date).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        });
      case 'name':
        return `${rowData.firstName} ${rowData.lastName}`;
      case 'customerName':
        return `${rowData.payerDetails?.name || ''}`;
      case 'userId':
        return rowData.userId || '';
      case 'tag':
        return rowData.tag || '';
      default:
        return rowData[columnDef.field] || '';
    }
  };

  const handleExportCsv = () => {
    const columnsToUse = userIsPaymentsCompany ? paymentCompanyColumns : columns
    const columnTitles = columnsToUse
      .filter(columnDef => !columnDef.hidden)
      .map(columnDef => columnDef.title);

    const csvData = transactions.map(rowData =>
      columnsToUse
        .filter(columnDef => !columnDef.hidden)
        .map(columnDef => getColumnValueForExport(rowData, columnDef))
    );

    const builder = new CsvBuilder('taptotip-payments.csv')
      .setDelimeter(',')
      .setColumns(columnTitles)
      .addRows(csvData)
      .exportFile();

    return builder;
  };

  const handleExportPDF = () => {
    const doc = new jsPDF('l');
    const columnWidths = {};
    const padding = 1;
    const defaultFontSize = doc.getFontSize();

    const columnsToUse = userIsPaymentsCompany ? paymentCompanyColumns : columns
    doc.setFontSize(10);

    const calculateColumnWidths = (cols, data, doc) => {
      cols.forEach((columnDef, index) => {
        if (columnDef.hidden) return; // Skip hidden columns
        let maxWidth = doc.getTextWidth(columnDef.title);

        data.forEach(rowData => {
          const cellValue = getColumnValueForExport(rowData, columnDef);
          maxWidth = Math.max(maxWidth, doc.getTextWidth(cellValue));
        });

        columnWidths[index] = maxWidth + padding;
      });
    };

    calculateColumnWidths(columnsToUse, transactions, doc);

    autoTable(doc, {
      theme: 'grid',
      head: [
        columnsToUse
          .filter(columnDef => !columnDef.hidden)
          .map(columnDef => ({ content: columnDef.title }))
      ],
      body: transactions.map(rowData =>
        columnsToUse
          .filter(columnDef => !columnDef.hidden)
          .map(columnDef => getColumnValueForExport(rowData, columnDef))
      )
    });

    doc.setFontSize(defaultFontSize);
    doc.save('taptotip-payments.pdf');
  };

  // Filter out columns for mobile
  let columnsToUse = userIsPaymentsCompany ? paymentCompanyColumns : columns
  const displayedColumns = isMobile ? columnsToUse.filter((_, index) => index < 5) : columnsToUse;

  // Card 237 — an admin who claims a tip gains a Stripe account, which used to
  // hand the page to the embedded per-account view for good and take the
  // company-wide list away with no route back. Both views stay reachable now.
  // `canSwitchView` is false for everyone who only ever had one of them, so
  // their page is unchanged.
  const showStripeConnectEmbedded =
    hasOwnStripePaymentsView(user) &&
    !(canSwitchView && paymentsView === PaymentsView.COMPANY_TRANSACTIONS);

  return (
    <>
      <TopNav />
      <div className={classes.outerContainer}>
        <TipDirectLeftMenu selectedTab={'Payments'} />
        <div className="flex-1 flex flex-col p-6 md:p-8 w-full max-w-7xl mx-auto min-w-0">
          <div className="mb-8 shrink-0 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
                {intl.formatMessage(messages.payments)}
              </h1>
              <p className="text-slate-500 mt-2">
                {intl.formatMessage(
                  showStripeConnectEmbedded
                    ? messages.ownStripeSubtitle
                    : messages.companyTransactionsSubtitle
                )}
              </p>
            </div>
            {canSwitchView && (
              <div
                role="group"
                data-whats-new="payments-view-switch"
                aria-label={intl.formatMessage(messages.viewSwitcherLabel)}
                className="flex flex-row bg-slate-100 border border-solid border-slate-200 rounded-xl p-1 shrink-0 w-full sm:w-fit">
                {[
                  { view: PaymentsView.OWN_STRIPE, label: messages.ownStripeView },
                  { view: PaymentsView.COMPANY_TRANSACTIONS, label: messages.companyTransactionsView }
                ].map(({ view, label }) => (
                  <button
                    key={view}
                    type="button"
                    aria-pressed={paymentsView === view}
                    onClick={() => setPaymentsView(view)}
                    className={`flex-1 sm:flex-none border-0 cursor-pointer font-semibold text-sm px-4 py-2 rounded-lg whitespace-nowrap transition-colors duration-150 ${
                      paymentsView === view
                        ? 'bg-white text-slate-900 shadow-sm'
                        : // slate-600 rather than the slate-500 used by the older
                          // switcher in PaymentOptionsSwitcher: on a slate-100
                          // track slate-500 measures 4.34:1, just under WCAG AA.
                          'bg-transparent text-slate-600 hover:bg-white'
                    }`}>
                    {intl.formatMessage(label)}
                  </button>
                ))}
              </div>
            )}
          </div>
          {!showStripeConnectEmbedded ? (
            <>
              <div className="w-full mb-4 flex flex-col items-center justify-center sm:flex-row sm:items-end sm:justify-end shrink-0">
                <AccessControlledButton
                  isHiddenWhenDisabled={true}
                  withIcon={false}
                  elementStyle="!bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !px-5 !py-2.5 !rounded-xl !shadow-sm !normal-case !whitespace-nowrap !h-fit !w-[90%] min-[480px]:!w-fit"
                  feature={'PaymentLinks'}
                  tooltipTitle=""
                  title={intl.formatMessage(messages.createPaymentLink)}
                  onClick={() => setIsPaymentLinkModalOpen(true)}
                />
              </div>
              {!transactions?.length && (
                <div className="flex flex-col justify-center items-center w-full sm:max-w-[418px] mb-4 bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 px-4 py-6 shrink-0">
                  <TextIconInformationBox card={TextIconBoxType.NoTipsYet} whiteBackground />
                </div>
              )}
              <div className="w-full h-fit bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 p-4 sm:p-6 shrink-0">
                <MaterialTable
                  isLoading={isLoading}
                  style={{ width: '100%', borderRadius: '16px', background: 'transparent' }}
                  columns={displayedColumns}
                  data={transactions}
                  title=""
                  components={{
                    // eslint-disable-next-line react/display-name
                    Container: props => (
                      <Paper
                        {...props}
                        elevation={0}
                        className={`${props.className || ''} ${classes.tableSurface}`}
                        style={{ ...props.style, background: 'transparent', boxShadow: 'none' }}
                      />
                    )
                  }}
                  actions={[
                    {
                      icon: () => <GridOnIcon />,
                      tooltip: 'Export to CSV',
                      onClick: () => handleExportCsv(),
                      isFreeAction: true
                    },
                    {
                      icon: () => <PictureAsPdfIcon />,
                      tooltip: 'Export to PDF',
                      onClick: () => handleExportPDF(),
                      isFreeAction: true
                    }
                  ]}
                  options={{
                    tableHeadStyle: {
                      whiteSpace: 'nowrap'
                    },
                    headerStyle: {
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      top: '0'
                    },
                    draggable: false,
                    cellStyle: {
                      whiteSpace: 'nowrap',
                      width: 'fit-content',
                      fontSize: '12px'
                    },
                    pageSizeOptions: [5, 10, 20, 30],
                    pageSize: 30,
                    emptyRowsWhenPaging: false,
                    maxBodyHeight: window.innerWidth > StyleBreakpoints.sm ? '75vh' : '55vh'
                  }}
                  localization={{
                    pagination: {
                      labelRowsSelect: intl.formatMessage(localizationMessages.labelRowsSelect),
                      labelRowsPerPage: intl.formatMessage(localizationMessages.labelRowsPerPage),
                      firstAriaLabel: intl.formatMessage(localizationMessages.firstAriaLabel),
                      firstTooltip: intl.formatMessage(localizationMessages.firstTooltip),
                      previousAriaLabel: intl.formatMessage(localizationMessages.previousAriaLabel),
                      previousTooltip: intl.formatMessage(localizationMessages.previousTooltip),
                      nextAriaLabel: intl.formatMessage(localizationMessages.nextAriaLabel),
                      nextTooltip: intl.formatMessage(localizationMessages.nextTooltip),
                      lastAriaLabel: intl.formatMessage(localizationMessages.lastAriaLabel),
                      lastTooltip: intl.formatMessage(localizationMessages.lastTooltip)
                    },
                    toolbar: {
                      searchTooltip: intl.formatMessage(localizationMessages.searchTooltip),
                      searchPlaceholder: intl.formatMessage(localizationMessages.searchPlaceholder)
                    },
                    body: {
                      emptyDataSourceMessage: intl.formatMessage(
                        localizationMessages.emptyDataSourceMessage
                      )
                    }
                  }}
                />
              </div>
            </>
          ) : (
            <div className="w-full h-fit bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 p-4 sm:p-6 shrink-0">
              <AccountPaymentsUI user={user} />
            </div>
          )}
        </div>
      </div>
      <PaymentLinkQRGeneratorModal
        isOpen={isPaymentLnkModalOpen}
        setIsOpen={setIsPaymentLinkModalOpen}
        currency={user.profile.currency}
        userId={user.userId}
      />
    </>
  );
};

const mapStateToProps = state => ({
  transactions: state.tipPayments?.transactions,
  isLoading: state.tipPayments?.isLoading,
  user: state.user?.user
});

const mapDispatchToProps = dispatch => ({
  fetchTransactions: userId => dispatch(actions.fetchTransactions({ userId }))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TippingPayments);
